import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Search, Unlock, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { seguridadAPI } from './api';
import { toast } from 'sonner';

const PAGE_SIZE = 20;

const scopeLabel = (s) => {
  if (s === 'ip_email') return 'IP + Email';
  if (s === 'ip') return 'IP';
  if (s === 'email') return 'Email';
  return s || '—';
};

const scopeBadge = (s) => {
  const map = {
    ip_email: 'bg-[#fbf7f0] text-[#9d8272] border-[#e8dfd0]',
    email: 'bg-amber-50 text-amber-700 border-amber-200',
    ip: 'bg-red-50 text-red-700 border-red-200',
  };
  return map[s] || 'bg-gray-100 text-gray-700 border-gray-200';
};

const remainingText = (blocked_until) => {
  const dt = new Date(blocked_until);
  const rem = Math.max(0, Math.floor((dt - Date.now()) / 1000));
  if (rem >= 3600) return `${Math.floor(rem / 3600)}h ${Math.floor((rem % 3600) / 60)}m`;
  if (rem >= 60) return `${Math.floor(rem / 60)}m ${rem % 60}s`;
  return `${rem}s`;
};

const BloqueosTab = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const h = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(h);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debounced]);

  const load = useCallback(() => {
    setLoading(true);
    seguridadAPI
      .blocks({ search: debounced, page, page_size: PAGE_SIZE })
      .then((r) => {
        setRows(r.data?.items || []);
        setTotal(r.data?.total || 0);
      })
      .catch(() => toast.error('Error al cargar bloqueos'))
      .finally(() => setLoading(false));
  }, [debounced, page]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const handleUnblock = (row) => {
    if (
      !window.confirm(
        `¿Desbloquear ${scopeLabel(row.block_scope)}: ${row.ip_address || ''} ${row.email || ''}?`
      )
    )
      return;
    seguridadAPI
      .unblock(row.block_scope, row.ip_address, row.email)
      .then(() => {
        toast.success('Desbloqueo realizado');
        load();
      })
      .catch(() => toast.error('Error al desbloquear'));
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4" data-testid="bloqueos-tab">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888888]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por IP o email..."
            className="pl-9"
            data-testid="blocks-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="blocks-refresh">
          <RefreshCw size={14} className="mr-1.5" />
          Refrescar
        </Button>
        <span className="text-xs text-[#888888]">
          {total} bloqueo(s) activo(s)
        </span>
      </div>

      <div className="bg-white border border-[#E8E8E7] rounded-lg overflow-hidden">
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <Loader2 size={28} className="animate-spin text-[#9d8272]" />
          </div>
        ) : rows.length === 0 ? (
          <div className="h-32 flex items-center justify-center text-sm text-[#888888]">
            No hay bloqueos activos.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="blocks-table">
              <thead className="bg-[#fbf7f0] border-b border-[#e8dfd0]">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-[#2D2D2D]">Scope</th>
                  <th className="text-left px-3 py-2 font-semibold text-[#2D2D2D]">IP</th>
                  <th className="text-left px-3 py-2 font-semibold text-[#2D2D2D]">Email</th>
                  <th className="text-left px-3 py-2 font-semibold text-[#2D2D2D]">Motivo</th>
                  <th className="text-left px-3 py-2 font-semibold text-[#2D2D2D]">Restante</th>
                  <th className="text-left px-3 py-2 font-semibold text-[#2D2D2D]">Bloqueado hasta</th>
                  <th className="text-right px-3 py-2 font-semibold text-[#2D2D2D]">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E8E7]">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-[#fafaf8]">
                    <td className="px-3 py-2">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${scopeBadge(
                          r.block_scope
                        )}`}
                      >
                        {scopeLabel(r.block_scope)}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.ip_address || '—'}</td>
                    <td className="px-3 py-2">{r.email || '—'}</td>
                    <td className="px-3 py-2 text-xs text-[#5A5A5A]">
                      {r.failure_reason || '—'}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-xs">
                      {remainingText(r.blocked_until)}
                    </td>
                    <td className="px-3 py-2 text-xs text-[#5A5A5A]">
                      {new Date(r.blocked_until).toLocaleString('es-PE')}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUnblock(r)}
                        data-testid={`unblock-${r.id}`}
                      >
                        <Unlock size={12} className="mr-1" />
                        Desbloquear
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs">
          <div className="text-[#888888]">
            Página {page} de {totalPages}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BloqueosTab;
